import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWallFeed, FeedFilter } from '../hooks/useWallFeed';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { WallPostCard } from '../components/WallPostCard';
import { PostComposer } from '../components/PostComposer';
import { ReactionFilter } from '../components/ReactionFilter';
import { useReactionTypes } from '../hooks/usePostReaction';

const FILTER_TABS: { value: FeedFilter; label: string }[] = [
  { value: 'all', label: 'For You' },
  { value: 'following', label: 'Following' },
];

export function FeedPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useCurrentUser();
  const { data: reactionTypes } = useReactionTypes();
  
  // Get initial values from URL params
  const initialVibe = searchParams.get('vibe');
  
  const [filter, setFilter] = useState<FeedFilter>('all');
  const [reactionFilter, setReactionFilter] = useState<string | null>(initialVibe);
  
  const { posts, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } = useWallFeed(filter, reactionFilter);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Update URL when reaction filter changes
  useEffect(() => {
    if (reactionFilter) {
      setSearchParams({ vibe: reactionFilter });
    } else {
      searchParams.delete('vibe');
      setSearchParams(searchParams);
    }
  }, [reactionFilter]);

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    observerRef.current = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  // Get active reaction info for empty state
  const activeReaction = reactionFilter 
    ? reactionTypes?.find(r => r.id === reactionFilter)
    : null;

  return (
    <div className="flex flex-col h-full">
      {/* Filter Tabs - Sticky */}
      <div className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="flex">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={cn(
                "flex-1 py-3 text-sm font-medium transition-colors relative",
                filter === tab.value
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
              {filter === tab.value && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-primary rounded-full" />
              )}
            </button>
          ))}
        </div>
        
        {/* Reaction Filter */}
        <div className="px-4 py-2 border-t border-border/50">
          <ReactionFilter 
            selectedReaction={reactionFilter}
            onSelectReaction={setReactionFilter}
          />
        </div>
      </div>

      {/* Post Composer - Only show if authenticated and no reaction filter */}
      {isAuthenticated && !reactionFilter && <PostComposer />}

      {/* Feed Content */}
      <div className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <p className="text-destructive text-center">Failed to load feed</p>
            <p className="text-muted-foreground text-sm text-center mt-1">
              Please try again later
            </p>
          </div>
        ) : posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            {reactionFilter && activeReaction ? (
              <>
                <span className="text-4xl mb-3">{activeReaction.emoji}</span>
                <p className="text-foreground font-medium text-center">
                  No {activeReaction.label} posts yet
                </p>
                <p className="text-muted-foreground text-sm text-center mt-1">
                  Be the first to earn a {activeReaction.emoji} reaction!
                </p>
                <button
                  onClick={() => setReactionFilter(null)}
                  className="mt-4 text-primary text-sm hover:underline"
                >
                  View all posts
                </button>
              </>
            ) : (
              <>
                <p className="text-foreground font-medium text-center">No posts yet</p>
                <p className="text-muted-foreground text-sm text-center mt-1">
                  {filter === 'following'
                    ? "Follow some athletes to see their posts here"
                    : "Be the first to post something!"}
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            {posts.map((post) => (
              <WallPostCard key={post.id} post={post} />
            ))}

            {/* Load More Trigger */}
            <div ref={loadMoreRef} className="py-4">
              {isFetchingNextPage && (
                <div className="flex justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}
              {!hasNextPage && posts.length > 0 && (
                <p className="text-center text-muted-foreground text-sm">
                  You've reached the end
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
